import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, Leaf, Loader2, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { SEOMeta } from '@/components/SEOMeta';

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) { navigate('/'); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error('Паролата трябва да е най-малко 6 символа'); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.full_name, refCode || null);
      toast.success('Акаунтът е създаден!');
      navigate('/calculator');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Грешка при регистрацията');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOMeta
        title="Регистрация"
        description="Създай безплатен акаунт в portion.bg и получи персонализиран хранителен план съобразен с твоите калории и цели."
        canonical="/register"
        noindex={true}
      />
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <motion.div className="w-full max-w-sm" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center mb-7">
            <img
              src="https://customer-assets.emergentagent.com/job_budget-meals-37/artifacts/4w0xeexu_Screenshot%202026-06-21%20at%2018.49.11-Photoroom.png"
              alt="Portion Budget Meals"
              className="h-44 w-auto object-contain drop-shadow-lg dark:brightness-0 dark:invert"
            />
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">Създай акаунт</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Безплатният калкулатор е винаги</p>
        </div>

        {refCode && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 flex items-center gap-2.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40 rounded-xl px-4 py-3"
          >
            <Gift className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">Поканен от приятел!</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-400">Получаваш +1 безплатна генерация</p>
            </div>
          </motion.div>
        )}

        <Card className="card-shadow rounded-xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="full_name" className="text-gray-900 dark:text-gray-200 text-sm font-medium">Име и фамилия</Label>
                <Input id="full_name" type="text" placeholder="Иван Иванов" value={form.full_name}
                  onChange={e => setForm(f => ({...f, full_name: e.target.value}))}
                  className="mt-1.5 h-11 border-[#E6EDF5] dark:border-slate-600 focus:border-emerald-500 dark:focus:border-emerald-400 dark:bg-slate-800 dark:text-gray-200" required />
              </div>
              <div>
                <Label htmlFor="email" className="text-gray-900 dark:text-gray-200 text-sm font-medium">Имейл</Label>
                <Input id="email" type="email" placeholder="you@example.com" value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  className="mt-1.5 h-11 border-[#E6EDF5] dark:border-slate-600 focus:border-emerald-500 dark:focus:border-emerald-400 dark:bg-slate-800 dark:text-gray-200" required />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-900 dark:text-gray-200 text-sm font-medium">Парола</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={showPw ? 'text' : 'password'} placeholder="мин. 6 символа" value={form.password}
                    onChange={e => setForm(f => ({...f, password: e.target.value}))}
                    className="h-11 pr-10 border-[#E6EDF5] dark:border-slate-600 focus:border-emerald-500 dark:focus:border-emerald-400 dark:bg-slate-800 dark:text-gray-200" required />
                  <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg p-3">
                <p className="text-xs text-amber-800 dark:text-amber-300">Медицински отказ: Не е подходящо за лица с диабет, инсулинова резистентност или други метаболитни нарушения.</p>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white min-h-11" data-testid="register-form-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Регистрирай се'}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
              Вече имаш акаунт?{' '}
              <Link to="/login" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium">Влезте</Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
    </>
  );
}
